import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import {
  FileListData,
  ThreeStepsDetails,
  CareStatusEnum,
} from '@typings/ResidentTypes';
import { changeCareStatus } from '@api/resident';
import { DateObjectType } from '@typings/common';
import { parseFormattedDate } from '@utils/commons';
import { LpaType } from './components/lpa/utils';
import { AdvanceDecisonType, CprDecisionType, AcmpType } from './utils';

interface UseStepTwoFormProps {
  slug?: string;
  threeStepsData: ThreeStepsDetails;
  setIsFormChanged: (isFormChanged: boolean) => void;
}

const useStepTwoForm = ({
  slug,
  threeStepsData,
  setIsFormChanged,
}: UseStepTwoFormProps) => {
  const [fileList, setFileList] = useState<FileListData[]>([]);
  const [careStatus, setCareStatus] = useState<CareStatusEnum | string>('');
  const [furtherCareStatus, setFurtherCareStatus] = useState('');
  const [roomNumberOrName, setRoomNumberOrName] = useState('');
  const [advanceCarePlan, setAdvanceCarePlan] = useState('');
  const [acpFurtherInformation, setAcpFurtherInformation] = useState('');
  const [residentFurtherInformation, setResidentFurtherInformation] =
    useState('');
  const [advanceDecision, setAdvanceDecision] = useState<AdvanceDecisonType>({
    status: '',
    updatedDate: { day: '', month: '', year: '' },
    location: '',
  });
  const [cprDecision, setCprDecision] = useState<CprDecisionType>({
    status: '',
    snomedCode: null,
    details: '',
    location: '',
    cprDocuments: [],
  });
  const [acmp, setAcmp] = useState<AcmpType>({
    status: '',
    updatedDate: { day: '', month: '', year: '' },
    location: '',
  });

  const [lpa, setLpa] = useState<LpaType>({
    healthAndWelfareLPAStatus: '',
    lpaInfo: {
      lpaLocation: '',
      lpaDeputies: '',
      lpaContact: {
        fullName: '',
        email: '',
        phoneNumber: '',
        relationship: '',
      },
      uploadedLpaFileList: [],
    },
  });

  const [lpaFromData, setLpaFromData] = useState<LpaType>({
    healthAndWelfareLPAStatus: '',
    lpaInfo: {
      lpaLocation: '',
      lpaDeputies: '',
      lpaContact: {
        fullName: '',
        email: '',
        phoneNumber: '',
        relationship: '',
      },
      uploadedLpaFileList: [],
    },
  });
  const [planUpdatedDate, setPlanUpdatedDate] = useState<DateObjectType>({
    day: '',
    month: '',
    year: '',
  });

  const getFieldValue = <T = string>(key: string): T =>
    (threeStepsData?.step2?.find(field => field.key === key)
      ?.answer as unknown as T) ?? ('' as unknown as T);

  useEffect(() => {
    if (!threeStepsData) return;

    setFileList(
      (threeStepsData.step2?.find(field => field.key === 'acpDocuments')
        ?.answer as FileListData[]) || []
    );
    setCareStatus(getFieldValue('careStatus'));
    setFurtherCareStatus(getFieldValue('furtherCareStatus'));
    setRoomNumberOrName(getFieldValue('roomNumberOrName'));
    setAdvanceCarePlan(getFieldValue('advanceCarePlan'));
    setAcpFurtherInformation(getFieldValue('acpFurtherInformation'));
    setResidentFurtherInformation(getFieldValue('residentFurtherInformation'));
    setPlanUpdatedDate(parseFormattedDate(getFieldValue('planUpdatedDate')));
    const advanceDecisionFromData = getFieldValue<{
      status: string;
      updatedDate: string;
      location: string;
    }>('advanceDecision');
    setAdvanceDecision({
      ...advanceDecisionFromData,
      updatedDate: parseFormattedDate(advanceDecisionFromData?.updatedDate),
    });
    setCprDecision(getFieldValue('cprDecision'));

    const acmpFromData = getFieldValue<{
      status: string;
      updatedDate: string;
      location: string;
    }>('acmp');
    setAcmp({
      ...acmpFromData,
      updatedDate: parseFormattedDate(acmpFromData?.updatedDate),
    });

    setLpaFromData({
      healthAndWelfareLPAStatus: getFieldValue('healthAndWelfareLPAStatus'),
      lpaInfo: {
        lpaLocation: getFieldValue('lpaLocation'),
        lpaDeputies: getFieldValue('lpaDeputies'),
        lpaContact: {
          fullName: getFieldValue('lpaFullName'),
          email: getFieldValue('lpaEmail'),
          phoneNumber: getFieldValue('lpaPhoneNumber'),
          relationship: getFieldValue('lpaRelationship'),
        },
        uploadedLpaFileList:
          (threeStepsData?.step2?.find(
            field => field.key === 'uploadedLpaFileList'
          )?.answer as FileListData[]) || [],
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threeStepsData]);

  const handleChangeCareStatus = async () => {
    if (slug) {
      try {
        await changeCareStatus(slug, careStatus);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const checkFormChanged = useCallback(() => {
    const currentFormValues = {
      careStatus,
      furtherCareStatus,
      roomNumberOrName,
      advanceCarePlan,
      acpFurtherInformation,
      residentFurtherInformation,
      fileList,
    };

    const formHasChanged = !_.isEqual(currentFormValues, threeStepsData);
    setIsFormChanged(formHasChanged);
  }, [
    careStatus,
    furtherCareStatus,
    roomNumberOrName,
    advanceCarePlan,
    acpFurtherInformation,
    residentFurtherInformation,
    fileList,
    threeStepsData,
    setIsFormChanged,
  ]);

  useEffect(() => {
    checkFormChanged();
  }, [checkFormChanged]);

  return {
    careStatus,
    setCareStatus,
    furtherCareStatus,
    setFurtherCareStatus,
    roomNumberOrName,
    setRoomNumberOrName,
    advanceCarePlan,
    setAdvanceCarePlan,
    acpFurtherInformation,
    setAcpFurtherInformation,
    residentFurtherInformation,
    setResidentFurtherInformation,
    fileList,
    setFileList,
    lpa,
    setLpa,
    handleChangeCareStatus,
    lpaFromData,
    setLpaFromData,
    planUpdatedDate,
    setPlanUpdatedDate,
    advanceDecision,
    setAdvanceDecision,
    cprDecision,
    setCprDecision,
    acmp,
    setAcmp,
  };
};

export default useStepTwoForm;
